// src/layout/MainLayout/Drawer/DrawerContent/index.jsx

import { useMemo } from 'react';

// material-ui
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

// project import
import NavGroup from './NavGroup';
import menuItem from 'menu-items';
import { menuItemDisabledBySettings } from 'menu-items/visibility';
import { useMenuVisibilitySettings } from 'menu-items/useMenuVisibilitySettings';
import useGamificationConfig from 'hooks/useGamificationConfig';

// RBAC imports (paths relative to THIS file)
import useRole from '../../../../../lib/auth/useRole';
import { menuItemHiddenByAdminMode, accessIsAllowed } from '../../../../../lib/auth/roles';
import { useAuth } from '../../../../../lib/auth';

// Recursively filter a menu node by role + feature flags + admin menu mode.
// - Admin sees everything allowed by roleIsAllowed.
// - Admins with adminMenuMode='management' hide host-facing duplicates.
// - For groups/collapses, we keep them only if they have at least one allowed child.
// - Items with featureKey (e.g. 'specialEventsMenuEnabled') are hidden if the flag is false.
function filterNode(node, userRole, flags, adminMenuMode, isSuperadmin, profile) {
  // 0) Superadmin-only items (platform admin — an Auth claim, NOT a role; the `admin` role does
  //    NOT satisfy this). Hidden from everyone without the claim, including admins.
  if (node.superadminOnly && !isSuperadmin) {
    return null;
  }

  if (menuItemDisabledBySettings(node, flags)) {
    return null;
  }

  // 1) Feature flag check (Special Events etc.)
  if (node.featureKey && !flags[node.featureKey]) {
    return null;
  }

  // 2) Access check: role OR any per-user access flag on the profile (e.g. canAccessManagementFrees)
  const permitted = accessIsAllowed(userRole, node.allowedRoles || [], profile, node.allowedProfileFlags);
  if (!permitted) return null;

  if (menuItemHiddenByAdminMode(node, userRole, adminMenuMode)) {
    return null;
  }

  // 3) If group/collapse, filter its children too
  if (node.type === 'group' || node.type === 'collapse') {
    const children = (node.children || [])
      .map((child) => filterNode(child, userRole, flags, adminMenuMode, isSuperadmin, profile))
      .filter(Boolean);

    if (children.length === 0) return null;
    return { ...node, children };
  }

  // item/divider/etc are fine if permitted
  return node;
}

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

export default function Navigation() {
  const userRole = useRole();
  const { profile, isSuperadminEffective } = useAuth() || {};
  const adminMenuMode = profile?.adminMenuMode || profile?.menuMode || profile?.adminMenuModeOverride || null;
  const { settings: featureFlags, ready: menuSettingsReady } = useMenuVisibilitySettings();
  // Gamification nav gating: brands without missions.enabled show no Missions
  // entry at all (config-driven per brand, like every other feature flag).
  // Waiting for the config read keeps the item from popping in/out on load.
  const { config: gamificationConfig, loading: gamificationLoading } = useGamificationConfig();
  const navReady = menuSettingsReady && !gamificationLoading;

  const filteredItems = useMemo(() => {
    if (!navReady) return [];
    const items = menuItem?.items || [];
    const flags = { ...featureFlags, missionsMenuEnabled: gamificationConfig.missions.enabled === true };
    return items.map((it) => filterNode(it, userRole, flags, adminMenuMode, isSuperadminEffective, profile)).filter(Boolean);
  }, [userRole, featureFlags, gamificationConfig.missions.enabled, adminMenuMode, isSuperadminEffective, navReady, profile]);

  if (!navReady) {
    return (
      <Box sx={{ pt: 2, px: 2 }}>
        {[0, 1, 2, 3, 4].map((index) => (
          <Skeleton key={index} variant="rounded" height={34} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  const navGroups = filteredItems.map((item) => {
    switch (item.type) {
      case 'group':
        return <NavGroup key={item.id} item={item} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });

  return <Box sx={{ pt: 2 }}>{navGroups}</Box>;
}
